import { create } from "zustand";
import {
  QuickActionTemplate,
  fetchQuickActionTemplates,
} from "../services/apiClientV2";
import { logDebug, logError } from "../utils/logging";
import { trackEvent } from "../services/analyticsService";
import { useToastStore } from "./toastStore";

// Type definitions for filtering
export type CategoryType = "Toate" | "Copywriting" | "Document" | "Altele" | "Sfaturi Strategice";
export type CardType = "creaza_draft" | "sfaturi_strategice" | "sugereste_curs";

// Category mapping with Romanian labels and backend IDs
const CATEGORY_CONFIG = {
  Toate: null, // Shows all templates
  Copywriting: "copywriting",
  Document: "documente", // Note: backend uses 'documente' not 'document'
  Altele: "altele",
} as const;

interface QuickActionsState {
  // State
  templates: QuickActionTemplate[];
  filteredTemplates: QuickActionTemplate[];
  selectedCategory: CategoryType;
  selectedCardType: CardType | null;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Actions
  loadTemplates: (
    cardType?: CardType | null,
    category?: CategoryType,
  ) => Promise<void>;
  setSelectedCategory: (category: CategoryType) => void;
  setSelectedCardType: (cardType: CardType | null) => void;
  filterTemplates: (
    cardType?: CardType | null,
    category?: CategoryType,
    templateList?: QuickActionTemplate[],
  ) => QuickActionTemplate[];
  getTemplateById: (templateId: string) => QuickActionTemplate | null;
  clearError: () => void;
}

export const useQuickActionsStore = create<QuickActionsState>((set, get) => ({
  // Initial state
  templates: [],
  filteredTemplates: [],
  selectedCategory: "Toate",
  selectedCardType: "creaza_draft",
  isLoading: false,
  error: null,
  isInitialized: false,

  // Load templates from API with optional filtering
  loadTemplates: async (cardType?: CardType | null, category?: CategoryType) => {
    const state = get();

    // Skip loading if already initialized and requested filter matches current state
    if (
      state.isInitialized &&
      !state.isLoading &&
      state.templates.length > 0 &&
      (!cardType || cardType === state.selectedCardType) &&
      (!category || category === state.selectedCategory)
    ) {
      logDebug(
        "[quickActionsStore] Templates already loaded, skipping API call",
      );
      const filtered = get().filterTemplates(
        state.selectedCardType,
        state.selectedCategory,
      );
      set({ filteredTemplates: filtered });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      logDebug("[quickActionsStore] Loading templates...", {
        cardType,
        category,
      });

      // Build API parameters
      const apiParams: Parameters<typeof fetchQuickActionTemplates>[0] = {};

      if (cardType && cardType !== "sugereste_curs") {
        // Map frontend cardType to backend card_type
        apiParams.card_type = cardType;
      }

      // Special override: If category is "Sfaturi Strategice", use card_type filtering instead
      if (category === "Sfaturi Strategice") {
        apiParams.card_type = "sfaturi_strategice";
        // We don't set apiParams.category here
      } else if (category && category !== "Toate") {
        // Map frontend category to backend category
        const backendCategory = CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG];
        if (backendCategory) {
          apiParams.category = backendCategory;
        }
      }

      const templates = await fetchQuickActionTemplates(apiParams);

      logDebug("[quickActionsStore] Templates loaded:", templates.length);

      // Track successful template loading
      trackEvent("quick_action_templates_loaded", {
        count: templates.length,
        card_type: cardType || "all",
        category: category || "all",
      });

      // Filter templates based on current selection
      const currentCardType = cardType || state.selectedCardType;
      const currentCategory = category || state.selectedCategory;
      // Set templates first, then filter
      set({ templates });
      const filtered = get().filterTemplates(currentCardType, currentCategory);

      set({
        templates,
        filteredTemplates: filtered,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Eroare la încărcarea șabloanelor";
      logError("[quickActionsStore] Error loading templates:", error);

      // Track template loading failure
      trackEvent("quick_action_templates_load_failed", {
        error: message,
        card_type: cardType || "all",
        category: category || "all",
      });

      // Show user-friendly toast once
      const { addToast } = useToastStore.getState();
      addToast({
        type: "error",
        message:
          "Nu am putut încărca șabloanele Smartie. Verificăm conexiunea...",
        action: {
          label: "Reîncearcă",
          onClick: () => {
            get()
              .loadTemplates(cardType, category)
              .catch(() => {
                /* error handled recursively */
              });
          },
        },
      });

      set({
        isLoading: false,
        error: message,
        isInitialized: true,
      });
    }
  },

  // Set selected category and update filtered templates
  setSelectedCategory: (category: CategoryType) => {
    logDebug("[quickActionsStore] Setting selected category:", category);

    const state = get();
    const filtered = get().filterTemplates(state.selectedCardType, category);

    // Track category selection
    trackEvent("quick_action_category_selected", {
      category,
      template_count: filtered.length,
    });

    set({
      selectedCategory: category,
      filteredTemplates: filtered,
    });
  },

  // Set selected card type and update filtered templates
  setSelectedCardType: (cardType: CardType | null) => {
    logDebug("[quickActionsStore] Setting selected card type:", cardType);

    const state = get();
    const filtered = get().filterTemplates(cardType, state.selectedCategory);

    // Track card type selection
    trackEvent("quick_action_card_type_selected", {
      card_type: cardType,
      template_count: filtered.length,
    });

    set({
      selectedCardType: cardType,
      filteredTemplates: filtered,
    });
  },

  // Filter templates based on card type and category
  filterTemplates: (
    cardType?: CardType | null,
    category?: CategoryType,
    templateList?: QuickActionTemplate[],
  ) => {
    const state = get();
    const templates = templateList || state.templates;

    let filtered = [...templates];

    // Filter by card type
    if (cardType && cardType !== "sugereste_curs") {
      filtered = filtered.filter((template) => template.card_type === cardType);
    }

    // Filter by category
    if (category && category !== "Toate") {
      // Special handling for "Sfaturi Strategice" pseudo-category
      if (category === "Sfaturi Strategice") {
        filtered = filtered.filter(t => t.card_type === "sfaturi_strategice");
      } else {
        const backendCategory = CATEGORY_CONFIG[category as keyof typeof CATEGORY_CONFIG];
        if (backendCategory) {
          filtered = filtered.filter(
            (template) => template.category === backendCategory,
          );
        }
      }
    }

    logDebug("[quickActionsStore] Filtered templates:", {
      cardType,
      category,
      originalCount: templates.length,
      filteredCount: filtered.length,
    });

    return filtered;
  },

  // Get template by ID
  getTemplateById: (templateId: string) => {
    const state = get();
    return (
      state.templates.find((template) => template.template_id === templateId) ||
      null
    );
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  },

  // Helper methods for better integration
  getTemplatesByCategory: (category: CategoryType) => {
    const state = get();
    return get().filterTemplates(
      state.selectedCardType,
      category,
      state.templates,
    );
  },

  getTemplatesByCardType: (cardType: CardType) => {
    const state = get();
    return get().filterTemplates(
      cardType,
      state.selectedCategory,
      state.templates,
    );
  },

  // Check if templates are loaded for specific criteria
  hasTemplatesLoaded: (cardType?: CardType, category?: CategoryType) => {
    const state = get();
    if (!state.isInitialized || state.templates.length === 0) {
      return false;
    }

    // If no specific criteria, just check if any templates are loaded
    if (!cardType && !category) {
      return true;
    }

    const filtered = get().filterTemplates(cardType, category, state.templates);
    return filtered.length > 0;
  },
}));

export default useQuickActionsStore;
